import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ArtifactType, getArtifactTypeLabel} from 'llm/chat-library/ArtifactTypes';
import {ChatContext} from 'llm/chat-library/ChatContext';
import {
  openSidebarItemDetail,
  setRightSidebarExpanded,
  setRightSidebarOpen,
  showSidebarList,
  useAISidepanelGlobalClientState,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';

import type {ChatLayoutProps} from '../ChatLayoutRegistry';
import {RightSidebarItemDetail} from './RightSidebarItemDetail';
import {RightSidebarListView} from './RightSidebarListView';
import type {SidebarItem} from './useSidebarItems';
import {useSidebarItems} from './useSidebarItems';

// The list view has a fixed width: it is not part of a resizable panel group and offers no drag
// handle. Only the detail view (below) is resizable.
const LIST_WIDTH = 320;

// Detail view width as a percentage of the layout. Drag-resizing the handle overrides this freely
// within [MIN_SIZE, MAX_SIZE].
const DEFAULT_SIZE = 60;
const MIN_SIZE = 20;
const MAX_SIZE = 80;

/**
 * Chat layout that docks artifacts in a resizable right sidebar rather than a fixed split screen.
 * Gated behind the LLM_ENABLE_RIGHT_SIDEBAR rollout flag (see ChatLayoutRegistry).
 *
 * The sidebar shows a list of the chat's artifacts (and any opened files); selecting one opens it in
 * a detail view with a back button to the list. The sidebar's open state and the selected artifact
 * are shared with the header toggle and the chat thread's draft-document button via the AI panel
 * state, so those controls can drive the sidebar. It can be resized by dragging its handle.
 */
export function RightSidebarLayout({
  chatContent,
  artifacts,
  sidebarData,
  isEditable,
  fullWidth = false,
}: ChatLayoutProps): JSX.Element {
  const {classes} = useStyles();
  const {primaryArtifact, canvasArtifact} = artifacts;
  const parentChatContext = useContext(ChatContext);
  const {items} = useSidebarItems({sidebarData});
  const chatMainRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const wasOverlaidRef = useRef(false);
  const chatFocusToRestoreRef = useRef<HTMLElement | null>(null);

  const [panelState] = useAISidepanelGlobalClientState();
  const isSidebarOpen = panelState.showRightSidebar;
  const selectedSidebarItemId = panelState.selectedSidebarItemId;
  // In the narrow (fullWidth) view the sidebar always covers the whole panel, so it is effectively
  // expanded and the expand control is not offered. In the split view, the detail-view expand control
  // toggles this to overlay the chat.
  const isExpanded = fullWidth || panelState.expandRightSidebar;

  // Auto-open the sidebar the first time content becomes available so output is not silently hidden,
  // but only on the rising edge — once open/closed, the user's toggle is respected. Skip this in the
  // collapsed (fullWidth) view, where the sidebar covers the whole panel over the chat: opening it on
  // a chat switch would hide the thread unprompted. The collapsed view defaults the sidebar closed.
  const hadItemsRef = useRef(false);
  useEffect(() => {
    const hasItems = items.length > 0;
    if (hasItems && !hadItemsRef.current && !fullWidth) {
      setRightSidebarOpen(true);
    }
    hadItemsRef.current = hasItems;
  }, [items.length, fullWidth]);

  const handleOpenFile = useCallback(
    (_chatSessionId: string, externalFileId: string, _filename: string, _mimeType: string) => {
      // Files are keyed in the sidebar list by their backend externalId (see useSidebarItems).
      openSidebarItemDetail(externalFileId);
    },
    []
  );

  const handleCloseSidebar = useCallback(() => {
    setRightSidebarOpen(false);
  }, []);

  const handleToggleExpand = useCallback(() => {
    setRightSidebarExpanded(!panelState.expandRightSidebar);
  }, [panelState.expandRightSidebar]);

  const contextValue = useMemo(
    () => ({
      openCanvas: parentChatContext?.openCanvas ?? (() => {}),
      openFile: handleOpenFile,
    }),
    [parentChatContext, handleOpenFile]
  );

  // Resolve the selected item. A stale id (e.g. an artifact that disappeared) falls back to the list.
  const selectedItem = useMemo((): SidebarItem | null => {
    const item = items.find((candidate) => candidate.id === selectedSidebarItemId);
    if (item != null) {
      return item;
    }
    // The backend lists only the session's current active canvas, but a canvas card earlier in the
    // thread can open any canvas. Opening one makes it the canvas artifact, so resolve its detail view
    // from that artifact directly. It is not added to the list: the backend still owns list membership.
    if (canvasArtifact != null && canvasArtifact.data.canvasDefinitionId === selectedSidebarItemId) {
      return {
        kind: 'artifact',
        section: 'outputs',
        id: canvasArtifact.data.canvasDefinitionId,
        label: getArtifactTypeLabel(ArtifactType.CANVAS),
        icon: 'document',
        artifactType: ArtifactType.CANVAS,
      };
    }
    // A newly created draft is opened as soon as it streams in, which can be before the backend's list
    // includes it, so likewise resolve its detail view from the document artifact.
    if (primaryArtifact != null && primaryArtifact.data.document_api_identifier === selectedSidebarItemId) {
      return {
        kind: 'artifact',
        section: 'outputs',
        id: primaryArtifact.data.document_api_identifier,
        label: getArtifactTypeLabel(ArtifactType.DOCUMENT),
        icon: 'document',
        artifactType: ArtifactType.DOCUMENT,
      };
    }
    return null;
  }, [items, selectedSidebarItemId, canvasArtifact, primaryArtifact]);

  const handleSelectItem = useCallback(
    (itemId: string) => {
      const item = items.find((candidate) => candidate.id === itemId);
      if (item?.kind === 'affected-object') {
        // External objects aren't openable in the Benchling editor; the list renders them as
        // non-interactive, so this is defensive.
        if (!item.openable) {
          return;
        }
        // Shrink the AI panel to its narrow view so the opened item's editor has room. Use unexpand
        // (not SidepanelActions.collapse(), which puts the panel in a state that only the Settings
        // sidepanel's own layout supports, at a width the AI panel's CSS doesn't match) and leave the
        // right sidebar open so it keeps overlaying the chat in that narrow view.
        SidepanelActions.unexpand({autoOpenFile: false});
        FileBrowserActions.openItem(item.externalId);
        return;
      }
      openSidebarItemDetail(itemId);
    },
    [items]
  );

  // An item resolved from an artifact can show even before the list has any items.
  const showSidebar = isSidebarOpen && (items.length > 0 || selectedItem != null);
  // An affected object never opens a detail view (see handleSelectItem), so it falls back to the list.
  const isListMode = selectedItem == null || selectedItem.kind === 'affected-object';

  const sidebar = (
    <aside
      ref={sidebarRef}
      // Focusable programmatically so focus can move into the sidebar when it covers the chat.
      tabIndex={-1}
      className={classes.sidebar}
      {...getTestProps({component: 'RightSidebarLayout', element: 'sidebar'})}
    >
      {isListMode ? (
        <RightSidebarListView items={items} onSelect={handleSelectItem} onClose={handleCloseSidebar} />
      ) : (
        <RightSidebarItemDetail
          item={selectedItem}
          primaryArtifact={primaryArtifact}
          canvasArtifact={canvasArtifact}
          isEditable={isEditable}
          onBack={showSidebarList}
          onClose={handleCloseSidebar}
          // The narrow view is always full-panel, so it offers no expand control; the split view does.
          canExpand={!fullWidth}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
        />
      )}
    </aside>
  );

  // The chat always renders at the same tree path (root > panel group > chat panel > chat-main), and
  // only the sidebar siblings after it vary by mode. React would otherwise remount the thread on
  // every open, close, select, or expand, losing thread-local state (scroll position, focus, unsent
  // input). The modes are:
  // - Expanded (the narrow fullWidth view, or the user expanded it): the sidebar overlays the chat.
  // - List: the list sits beside the chat panel group at a fixed width and is not resizable.
  // - Detail: the sidebar joins the panel group as a resizable panel with a drag handle.
  const showOverlaySidebar = showSidebar && isExpanded;
  const showFixedSidebar = showSidebar && !isExpanded && isListMode;
  const showResizableSidebar = showSidebar && !isExpanded && !isListMode;

  // While the sidebar overlays the chat, make the covered chat inert so keyboard and screen-reader
  // users can't reach controls hidden beneath it. React 17 has no `inert` prop, so set the attribute
  // directly. This runs before paint, and reads focus before applying inert, which blurs whatever
  // was focused in the chat.
  useLayoutEffect(() => {
    const chatMain = chatMainRef.current;
    if (chatMain == null) {
      return;
    }
    const wasOverlaid = wasOverlaidRef.current;
    wasOverlaidRef.current = showOverlaySidebar;
    const activeElement = document.activeElement;
    const isFocusInChat = activeElement instanceof HTMLElement && chatMain.contains(activeElement);
    // Moving the sidebar between the panel group and the overlay remounts it, dropping its focus.
    const isFocusLost = activeElement == null || activeElement === document.body;

    chatMain.toggleAttribute('inert', showOverlaySidebar);

    if (showOverlaySidebar && !wasOverlaid) {
      // Remember where the user was in the chat so closing the overlay returns them there, and move
      // focus into the sidebar rather than leaving it on a hidden control or the page body.
      if (isFocusInChat) {
        chatFocusToRestoreRef.current = activeElement;
      }
      if (isFocusInChat || isFocusLost) {
        sidebarRef.current?.focus();
      }
    } else if (!showOverlaySidebar && wasOverlaid) {
      const chatFocusToRestore = chatFocusToRestoreRef.current;
      chatFocusToRestoreRef.current = null;
      if (!isFocusLost) {
        return;
      }
      if (showSidebar) {
        // Collapsed back to the split view: the user was working in the sidebar, so keep them there.
        sidebarRef.current?.focus();
      } else if (chatFocusToRestore?.isConnected) {
        chatFocusToRestore.focus();
      }
    }
  }, [showOverlaySidebar, showSidebar]);

  return (
    <ChatContext.Provider value={contextValue}>
      <div
        className={classes.container}
        {...getTestProps({component: 'RightSidebarLayout', element: 'root'})}
      >
        <PanelGroup direction="horizontal" className={classes.panelGroup}>
          {/* Panels that mount conditionally need a stable id and order (react-resizable-panels). */}
          <Panel id="chat" order={1} minSize={MIN_SIZE} className={classes.chatPanel}>
            <div
              ref={chatMainRef}
              className={classes.chatMain}
              {...getTestProps({component: 'RightSidebarLayout', element: 'chat-main'})}
            >
              {chatContent}
            </div>
          </Panel>

          {showResizableSidebar && (
            <>
              <PanelResizeHandle className={classes.resizer}>
                <div
                  className={classes.resizerHandle}
                  {...getTestProps({component: 'RightSidebarLayout', element: 'resizer'})}
                />
              </PanelResizeHandle>

              <Panel
                id="sidebar"
                order={2}
                defaultSize={DEFAULT_SIZE}
                minSize={MIN_SIZE}
                maxSize={MAX_SIZE}
                className={classes.sidebarPanel}
              >
                {sidebar}
              </Panel>
            </>
          )}
        </PanelGroup>
        {showFixedSidebar && <div className={classes.fixedSidebar}>{sidebar}</div>}
        {showOverlaySidebar && <div className={classes.overlay}>{sidebar}</div>}
      </div>
    </ChatContext.Provider>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
  },
  panelGroup: {
    flex: 1,
    minWidth: 0,
    height: '100%',
  },
  chatPanel: {
    height: '100%',
  },
  chatMain: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  sidebarPanel: {
    height: '100%',
  },
  fixedSidebar: {
    flexShrink: 0,
    width: LIST_WIDTH,
    height: '100%',
  },
  sidebar: {
    flex: 1,
    width: '100%',
    height: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.white,
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
    // The sidebar only takes focus programmatically, as a landing spot; it isn't a control, so it
    // shows no focus ring. Its controls keep their own.
    '&:focus': {
      outline: 'none',
    },
  },
  resizer: {
    width: 12,
    flexShrink: 0,
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  resizerHandle: {
    width: 2,
    height: 40,
    borderRadius: 2,
    backgroundColor: theme.colors.gray[4],
  },
}));
